import { useState, useEffect, useRef, useCallback } from 'react';

export type SSEConnectionStatus = 'connected' | 'polling' | 'disconnected';

export interface SSEEvent {
  type: string;
  status: string | null;
  timestamp: string;
  [key: string]: unknown;
}

export interface UseSSEOptions {
  apiKey?: string;
  maxEvents?: number;
  pollingInterval?: number;
  pollingUrl?: string;
  onEvent?: (event: SSEEvent) => void;
}

export interface UseSSEResult {
  events: SSEEvent[];
  connected: SSEConnectionStatus;
  lastEvent: SSEEvent | null;
  lastUpdated: Date | null;
}

/**
 * React hook for consuming Server-Sent Events from the governance event stream.
 * Falls back to polling if SSE connection fails (graceful degradation).
 */
export function useSSE(url: string, options?: UseSSEOptions): UseSSEResult {
  const {
    apiKey,
    maxEvents = 50,
    pollingInterval = 15000,
    pollingUrl,
    onEvent,
  } = options || {};

  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [connected, setConnected] = useState<SSEConnectionStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<SSEEvent | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const addEvent = useCallback((event: SSEEvent) => {
    setEvents(prev => {
      const next = [...prev, event];
      if (next.length > maxEvents) {
        return next.slice(next.length - maxEvents);
      }
      return next;
    });
    setLastEvent(event);
    setLastUpdated(new Date());
    if (onEventRef.current) {
      onEventRef.current(event);
    }
  }, [maxEvents]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    const fetchUrl = pollingUrl || url.replace('/stream', '/recent');
    const poll = async () => {
      try {
        const headers: Record<string, string> = { 'Accept': 'application/json' };
        if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
        const res = await fetch(fetchUrl, { headers });
        if (res.ok) {
          const data = await res.json();
          const newEvents: SSEEvent[] = data.events || [];
          if (newEvents.length > 0) {
            setEvents(newEvents.slice(-maxEvents));
            setLastEvent(newEvents[newEvents.length - 1] ?? null);
            setLastUpdated(new Date());
          }
          setConnected('polling');
        }
      } catch {
        // polling failed, stay in current state
      }
    };
    poll();
    pollingRef.current = setInterval(poll, pollingInterval);
    setConnected('polling');
  }, [pollingUrl, url, apiKey, maxEvents, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const connectSSE = useCallback(() => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const sseUrl = apiKey ? `${url}?apiKey=${encodeURIComponent(apiKey)}` : url;
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnected('connected');
        stopPolling();
      };

      es.onmessage = (event) => {
        try {
          const parsed: SSEEvent = JSON.parse(event.data);
          addEvent(parsed);
        } catch {
          // ignore unparseable messages (keepalive comments are not dispatched as messages)
        }
      };

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        setConnected('disconnected');
        // Fall back to polling
        startPolling();
        // Attempt reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          stopPolling();
          connectSSE();
        }, 5000);
      };
    } catch {
      // EventSource not supported or URL invalid - fall back to polling
      startPolling();
    }
  }, [url, apiKey, addEvent, startPolling, stopPolling]);

  useEffect(() => {
    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      stopPolling();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connectSSE, stopPolling]);

  return { events, connected, lastEvent, lastUpdated };
}
